import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Paper, Typography, useTheme } from '@mui/material';
import {
  Build as EngineIcon,
  Business as OrganizationIcon,
  Category as FeaturesIcon,
  CreditCard as VinIcon,
  Inventory2 as CapacityIcon,
  Label as PlateIcon,
  LocalGasStation as FuelIcon,
  Payments as PaymentsIcon,
} from '@mui/icons-material';
import type { TruckSchema } from '../../types/transportation.type';

const SAR_SYMBOL_SRC = '/images/Saudi_Riyal_Symbol.svg';

function formatCapacity(vehicle: TruckSchema): string {
  const unit = vehicle.data?.capacity_unit;
  if (unit) {
    return `${vehicle.capacity.toLocaleString()} ${unit}`;
  }
  return vehicle.capacity.toLocaleString();
}

function formatPricePerTrip(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function InfoCard({
  icon,
  label,
  children,
  fullWidth,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
  fullWidth?: boolean;
}) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        gridColumn: fullWidth ? '1 / -1' : undefined,
        p: 1.5,
        borderRadius: 1,
        bgcolor: theme.palette.background.default,
        border: '1px solid',
        borderColor: theme.palette.divider,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
      }}
    >
      <Box sx={{ flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        {children}
      </Box>
    </Paper>
  );
}

export interface TruckMainDataPanelProps {
  truck: TruckSchema;
  organizationName?: string;
}

/**
 * Read-only "Main data" view of a truck's fields.
 *
 * Factored out of the retired `TruckDetailsModal`'s "Information" sub-tab so
 * the same content can be reused as the Main Data tab of `TruckDetails.tsx`.
 */
export default function TruckMainDataPanel({ truck, organizationName = '' }: TruckMainDataPanelProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const iconColor = theme.palette.text.secondary;
  const vehicleType = truck.data?.vehicle_type || 'Truck';

  const pricePerTrip = typeof truck.data?.price_per_trip === 'number' ? truck.data.price_per_trip : null;
  const truckFeatures = Array.isArray(truck.data?.truck_features) ? (truck.data!.truck_features as string[]) : [];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('components.truckDetails.vehicleInfo', { type: vehicleType })}
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
        <InfoCard icon={<VinIcon sx={{ color: iconColor, fontSize: 28 }} />} label={t('transportation.fleet.vin')}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {truck.data?.vin || '—'}
          </Typography>
        </InfoCard>

        <InfoCard icon={<CapacityIcon sx={{ color: iconColor, fontSize: 28 }} />} label={t('components.truckDetails.capacity')}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {formatCapacity(truck)}
          </Typography>
        </InfoCard>

        <InfoCard icon={<FuelIcon sx={{ color: iconColor, fontSize: 28 }} />} label={t('transportation.fleet.fuelConsumption')}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {typeof truck.data?.fuel_consumption_km_per_l === 'number'
              ? `${truck.data.fuel_consumption_km_per_l} KM/L`
              : '—'}
          </Typography>
        </InfoCard>

        <InfoCard icon={<EngineIcon sx={{ color: iconColor, fontSize: 28 }} />} label={t('transportation.fleet.engineType')}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {truck.data?.engine_type
              ? String(truck.data.engine_type).charAt(0).toUpperCase() + String(truck.data.engine_type).slice(1)
              : '—'}
          </Typography>
        </InfoCard>

        <InfoCard
          fullWidth
          icon={<PaymentsIcon sx={{ color: iconColor, fontSize: 28 }} />}
          label={t('transportation.fleet.pricePerTrip')}
        >
          {pricePerTrip != null ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Box
                component="img"
                src={SAR_SYMBOL_SRC}
                alt=""
                sx={(t) => ({
                  width: 24,
                  height: 'auto',
                  display: 'block',
                  ...(t.palette.mode === 'dark'
                    ? { opacity: 0.5, filter: 'brightness(0) invert(1)' }
                    : { opacity: 0.48, filter: 'brightness(0)' }),
                })}
              />
              <Typography variant="body1" fontWeight={600}>
                {formatPricePerTrip(pricePerTrip)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body1" fontWeight={600}>
              —
            </Typography>
          )}
        </InfoCard>

        <InfoCard
          fullWidth
          icon={<FeaturesIcon sx={{ color: iconColor, fontSize: 28 }} />}
          label={t('transportation.fleet.truckFeatures')}
        >
          {truckFeatures.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {truckFeatures.map((feature, index) => (
                <Chip key={`${feature}-${index}`} label={feature} size="small" variant="outlined" />
              ))}
            </Box>
          ) : (
            <Typography variant="body1" fontWeight={600}>
              —
            </Typography>
          )}
        </InfoCard>
      </Box>

      <Box sx={{ mt: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
        <InfoCard icon={<OrganizationIcon sx={{ color: iconColor, fontSize: 28 }} />} label={t('common.organization')}>
          <Typography variant="body1" fontWeight={600} noWrap>
            {organizationName || '—'}
          </Typography>
        </InfoCard>

        <InfoCard icon={<PlateIcon sx={{ color: iconColor, fontSize: 28 }} />} label={t('components.truckDetails.licensePlate')}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="body1" fontWeight={600}>
              {truck.data?.ar_plate_number || '—'}
            </Typography>
            <Typography variant="body1" fontWeight={600} color="text.secondary">
              /
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {truck.data?.en_plate_number || '—'}
            </Typography>
          </Box>
        </InfoCard>
      </Box>
    </Box>
  );
}
